import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { PostContext } from '../types/chat';
import { CarouselCardsPreview } from './carousel/CarouselCardsPreview';
import DOMPurify from 'dompurify';
import { toPng } from 'html-to-image';
import { RichModalEditor, domPurifyConfig, ensureFontsLoaded, extractPrimaryFontsFromHtml } from './content-plan/post-chat/RichModalEditor';

let domPurifySuggestionHooksInstalled = false;
const installSuggestionDomPurifyHooks = () => {
  if (domPurifySuggestionHooksInstalled) return;
  domPurifySuggestionHooksInstalled = true;

  // Разрешаем <img> только с src из нашего S3 и чистим style на <img>
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const tagName = (node as any)?.tagName?.toUpperCase?.() || '';
    const attrName = (data as any)?.attrName || '';
    const attrValue = String((data as any)?.attrValue ?? '');

    if (tagName === 'IMG' && attrName === 'src') {
      if (!attrValue.startsWith('https://s3.twcstorage.ru/')) {
        (data as any).keepAttr = false;
      }
      return;
    }

    if (tagName === 'IMG' && attrName === 'style') {
      const allowedProps = new Set([
        'position',
        'top',
        'left',
        'right',
        'bottom',
        'z-index',
        'width',
        'height',
        'object-fit',
        'object-position',
        'border-radius',
        'border-top-left-radius',
        'border-top-right-radius',
        'border-bottom-left-radius',
        'border-bottom-right-radius',
        'transform',
        'opacity',
        'filter',
        'display',
        'flex',
        'flex-direction',
        'justify-content',
        'align-items',
        'margin',
        'margin-top',
        'margin-bottom',
        'margin-left',
        'margin-right',
        'padding',
        'padding-top',
        'padding-bottom',
        'padding-left',
        'padding-right',
        'border',
        'border-left',
        'border-right',
        'border-top',
        'border-bottom',
        'box-sizing',
        'background',
        'background-color',
        'background-image',
        'background-size',
        'background-position',
        'background-repeat',
        'font-family',
        'font-size',
        'font-weight',
        'line-height',
        'letter-spacing',
        'text-transform',
        'text-align',
        'overflow',
        'gap',
        'box-shadow',
        'color',
      ]);

      const safe = attrValue
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)
        .map((decl) => {
          const idx = decl.indexOf(':');
          if (idx <= 0) return null;
          const key = decl.slice(0, idx).trim().toLowerCase();
          const val = decl.slice(idx + 1).trim();
          if (!allowedProps.has(key)) return null;
          if (!val) return null;
          return `${key}: ${val}`;
        })
        .filter(Boolean)
        .join('; ');

      (data as any).attrValue = safe;
    }
  });
};

installSuggestionDomPurifyHooks();

interface PostSuggestion {
  title?: string;
  idea?: string;
  content?: string;
  time?: string;
  carouselHtmlJson?: string;
  coverCardHtml?: string;
}

interface PostSuggestionPreviewProps {
  messageId: string;
  messageContent: string;
  onApply: (suggestions: PostSuggestion) => void;
  onUpdateMessageContent?: (messageId: string, nextContent: string) => void;
  onRevert?: (field: keyof PostSuggestion) => void;
  originalValues?: Partial<PostContext>;
  appliedChanges?: Record<string, boolean>;
  onSwitchToPreview?: () => void;
  disabled?: boolean;
}

// Функция для парсинга SUGGEST тегов из текста
const parseSuggestions = (content: string): PostSuggestion | null => {
  // Проверяем, что content не undefined и является строкой
  if (!content || typeof content !== 'string') {
    return null;
  }

  const suggestions: PostSuggestion = {};
  let hasSuggestions = false;

  const patterns = [
    { tag: 'SUGGEST_TITLE', field: 'title' as keyof PostSuggestion },
    { tag: 'SUGGEST_IDEA', field: 'idea' as keyof PostSuggestion },
    { tag: 'SUGGEST_CONTENT', field: 'content' as keyof PostSuggestion },
    { tag: 'SUGGEST_TIME', field: 'time' as keyof PostSuggestion },
    { tag: 'SUGGEST_CAROUSEL', field: 'carouselHtmlJson' as keyof PostSuggestion },
    { tag: 'SUGGEST_COVER_CARD', field: 'coverCardHtml' as keyof PostSuggestion },
  ];

  patterns.forEach(({ tag, field }) => {
    const startTag = `[${tag}]`;
    const endTag = `[/${tag}]`;
    const startIndex = content.indexOf(startTag);
    
    if (startIndex >= 0) {
      const endIndex = content.indexOf(endTag, startIndex);
      if (endIndex > startIndex) {
        const value = content.substring(startIndex + startTag.length, endIndex).trim();
        if (value) {
          suggestions[field] = value;
          hasSuggestions = true;
        }
      }
    }
  });

  return hasSuggestions ? suggestions : null;
};

const extractTagContent = (content: string, tag: string): string | null => {
  if (!content || typeof content !== 'string') return null;
  const startTag = `[${tag}]`;
  const endTag = `[/${tag}]`;
  const startIndex = content.indexOf(startTag);
  if (startIndex < 0) return null;
  const endIndex = content.indexOf(endTag, startIndex);
  if (endIndex <= startIndex) return null;
  const value = content.substring(startIndex + startTag.length, endIndex).trim();
  return value || null;
};

const replaceTagContent = (content: string, tag: string, nextValue: string): string => {
  if (!content || typeof content !== 'string') return '';
  const startTag = `[${tag}]`;
  const endTag = `[/${tag}]`;
  const startIndex = content.indexOf(startTag);
  if (startIndex < 0) {
    // если тега нет — дописываем в конец
    const sep = content.trim().length > 0 ? '\n\n' : '';
    return `${content}${sep}${startTag}\n${nextValue}\n${endTag}`.trim();
  }
  const endIndex = content.indexOf(endTag, startIndex);
  if (endIndex <= startIndex) return content;
  const before = content.substring(0, startIndex + startTag.length);
  const after = content.substring(endIndex);
  return `${before}\n${nextValue}\n${after}`;
};

// Функция для очистки текста от SUGGEST тегов
const cleanMessageContent = (content: string): string => {
  // Проверяем, что content не undefined и является строкой
  if (!content || typeof content !== 'string') {
    return '';
  }

  let cleaned = content;
  
  const tags = ['SUGGEST_TITLE', 'SUGGEST_IDEA', 'SUGGEST_CONTENT', 'SUGGEST_TIME', 'SUGGEST_CAROUSEL'];
  // cover card tag (preview only)
  tags.push('SUGGEST_COVER_CARD');
  
  tags.forEach(tag => {
    const startTag = `[${tag}]`;
    const endTag = `[/${tag}]`;
    
    let startIndex = cleaned.indexOf(startTag);
    while (startIndex >= 0) {
      const endIndex = cleaned.indexOf(endTag, startIndex);
      if (endIndex > startIndex) {
        cleaned = cleaned.substring(0, startIndex) + cleaned.substring(endIndex + endTag.length);
      } else {
        break;
      }
      startIndex = cleaned.indexOf(startTag);
    }
  });
  
  return cleaned.trim();
};

// CarouselCardsPreview вынесен в общий компонент: ./carousel/CarouselCardsPreview

export const PostSuggestionPreview: React.FC<PostSuggestionPreviewProps> = ({
  messageId,
  messageContent,
  onApply,
  onUpdateMessageContent,
  onRevert,
  originalValues,
  appliedChanges,
  onSwitchToPreview,
  disabled = false
}) => {
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({});
  const [showPreview, setShowPreview] = useState(true);
  const [carouselDraftJson, setCarouselDraftJson] = useState<string | null>(null);
  const [carouselDirty, setCarouselDirty] = useState(false);
  const [carouselPreviewIndex, setCarouselPreviewIndex] = useState<number>(0);
  const [carouselEditorOpen, setCarouselEditorOpen] = useState(false);
  const [carouselEditorIndex, setCarouselEditorIndex] = useState<number>(0);
  const [carouselEditorHtml, setCarouselEditorHtml] = useState<string>('');
  const [carouselEditorSaving, setCarouselEditorSaving] = useState(false);
  const [isDownloadingCarousel, setIsDownloadingCarousel] = useState(false);
  const carouselExportId = `carousel-export-${messageId}`;
  const [coverEditorOpen, setCoverEditorOpen] = useState(false);
  const [coverEditorHtml, setCoverEditorHtml] = useState<string>('');
  const [coverEditorError, setCoverEditorError] = useState<string | null>(null);
  const [isDownloadingCover, setIsDownloadingCover] = useState(false);
  const [coverLightboxOpen, setCoverLightboxOpen] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const coverExportRootId = `cover-export-${messageId}`;

  const suggestions = React.useMemo(() => parseSuggestions(messageContent), [messageContent]);
  const coverCardHtml = React.useMemo(() => extractTagContent(messageContent, 'SUGGEST_COVER_CARD'), [messageContent]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const families = [];
        if (coverCardHtml) {
          families.push(...extractPrimaryFontsFromHtml(coverCardHtml));
        }
        if (suggestions?.carouselHtmlJson) {
          try {
            const cards = JSON.parse(suggestions.carouselHtmlJson);
            if (Array.isArray(cards)) {
              cards.forEach(c => {
                if (typeof c === 'string') families.push(...extractPrimaryFontsFromHtml(c));
              });
            }
          } catch (e) {}
        }

        if (families.length > 0) {
          await ensureFontsLoaded(Array.from(new Set(families)));
        }
      } catch (e) {
        console.error('Error loading fonts for suggestions', e);
      } finally {
        if (!cancelled) setFontsReady(true);
      }
    };
    setFontsReady(false);
    void run();
    return () => { cancelled = true; };
  }, [coverCardHtml, suggestions?.carouselHtmlJson]);
  const cleanContent = React.useMemo(() => cleanMessageContent(messageContent), [messageContent]);

  const carouselOriginalJson = useMemo(() => {
    const raw = suggestions?.carouselHtmlJson;
    return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
  }, [suggestions?.carouselHtmlJson]);

  // Сбрасываем черновик при изменении сообщения (включая после сохранения)
  useEffect(() => {
    setCarouselDraftJson(carouselOriginalJson);
    setCarouselDirty(false);
    setCarouselEditorOpen(false);
    setCarouselEditorIndex(0);
    setCarouselEditorHtml('');
    setCarouselEditorSaving(false);
    setCarouselPreviewIndex(0);
    setCoverEditorOpen(false);
    setCoverEditorError(null);
  }, [carouselOriginalJson, messageId]);

  useEffect(() => {
    setCoverEditorOpen(false);
    setCoverEditorError(null);
  }, [coverCardHtml, messageId]);

  const extractFirstImgSrc = (html: string): string | null => {
    if (!html) return null;
    const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    const src = m?.[1]?.trim();
    return src && src.startsWith('https://') ? src : null;
  };

  if (!suggestions) {
    // Если нет предложений, просто показываем очищенный текст
    return <span className="whitespace-pre-wrap">{cleanContent}</span>;
  }

  // Инициализируем выбранные поля (по умолчанию все выбраны)
  React.useEffect(() => {
    if (suggestions) {
      const initialSelected: Record<string, boolean> = {};
      Object.keys(suggestions).forEach(field => {
        initialSelected[field] = true;
      });
      
      // Обновляем состояние только если есть изменения
      setSelectedFields(prev => {
        const hasChanges = Object.keys(initialSelected).some(key => 
          !(key in prev) || prev[key] !== initialSelected[key]
        ) || Object.keys(prev).length !== Object.keys(initialSelected).length;
        
        return hasChanges ? initialSelected : prev;
      });
    } else {
      // Если suggestions нет, очищаем выбранные поля
      setSelectedFields(prev => Object.keys(prev).length > 0 ? {} : prev);
    }
  }, [messageContent]); // Используем messageContent вместо suggestions

  const handleFieldToggle = (field: string) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleApply = React.useCallback(() => {
    const selectedSuggestions: PostSuggestion = {};
    Object.entries(suggestions || {}).forEach(([field, value]) => {
      if (selectedFields[field] && value) {
        (selectedSuggestions as any)[field] = value;
      }
    });

    // Дополнение: если применяем coverCardHtml, то также проставим coverImage как src первого <img> (для обратной совместимости)
    if ((selectedSuggestions as any).coverCardHtml) {
      const src = extractFirstImgSrc(String((selectedSuggestions as any).coverCardHtml));
      if (src) {
        (selectedSuggestions as any).coverImage = src;
      }
    }
    onApply(selectedSuggestions);
    if (onSwitchToPreview) {
      onSwitchToPreview();
    }
  }, [suggestions, selectedFields, onApply, onSwitchToPreview]);

  const handleCoverEdit = () => {
    if (!coverCardHtml) return;
    setCoverEditorError(null);
    setCoverEditorHtml(coverCardHtml);
    setCoverEditorOpen(true);
  };

  const handleCoverSave = async (nextRawHtml: string) => {
    if (!onUpdateMessageContent || !messageId) return;
    setCoverEditorError(null);
    try {
      const safe = DOMPurify.sanitize(nextRawHtml, domPurifyConfig) as unknown as string;
      const nextContent = replaceTagContent(messageContent, 'SUGGEST_COVER_CARD', safe);
      onUpdateMessageContent(messageId, nextContent);
    setCoverEditorOpen(false);
    } catch (e) {
      setCoverEditorError('Ошибка при сохранении обложки');
      console.error(e);
    }
  };

  const handleDownloadCover = async () => {
    if (isDownloadingCover) return;
    if (!coverCardHtml) return;
    setIsDownloadingCover(true);
    try {
      const el = document.getElementById(coverExportRootId);
      if (!(el instanceof HTMLElement)) return;
      const dataUrl = await toPng(el, { width: 1080, height: 1920 });
      const link = document.createElement('a');
      link.download = `reels-cover.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error('Failed to download cover', e);
    } finally {
      setIsDownloadingCover(false);
    }
  };

  const getFieldLabel = React.useCallback((field: string): string => {
    const labels: Record<string, string> = {
      title: 'Заголовок',
      idea: 'Идея',
      content: 'Контент',
      time: 'Время',
      carouselHtmlJson: 'Карусель',
      coverCardHtml: 'Обложка Reels'
    };
    return labels[field] || field;
  }, []);

  const handleRevert = React.useCallback((field: keyof PostSuggestion) => {
    if (onRevert) {
      onRevert(field);
    }
  }, [onRevert]);

  const hasSelectedFields = React.useMemo(() => 
    Object.values(selectedFields).some(Boolean), 
    [selectedFields]
  );

  const parseCarouselCards = (json: string): string[] | null => {
    try {
      const parsed = JSON.parse(json);
      if (!Array.isArray(parsed)) return null;
      const cards = parsed.filter((x): x is string => typeof x === 'string');
      return cards.length > 0 ? cards : [];
    } catch {
      return null;
    }
  };

  const handleCarouselEditCard = (index: number) => {
    const draft = carouselDraftJson ?? carouselOriginalJson;
    if (!draft) return;
    const cards = parseCarouselCards(draft);
    if (!cards) return;
    const safeIndex = Math.max(0, Math.min(index, Math.max(0, cards.length - 1)));
    setCarouselEditorIndex(safeIndex);
    setCarouselEditorHtml(cards[safeIndex] || '');
    setCarouselEditorOpen(true);
  };

  const handleCarouselSaveCard = async (nextRawHtml: string) => {
    const draft = carouselOriginalJson; // Берем оригинал из сообщения
    if (!draft || !onUpdateMessageContent) return;
    const cards = parseCarouselCards(draft);
    if (!cards) return;
    const nextCards = [...cards];
    if (carouselEditorIndex < 0 || carouselEditorIndex >= nextCards.length) return;
    
    setCarouselEditorSaving(true);
    try {
      const safe = DOMPurify.sanitize(nextRawHtml, domPurifyConfig) as unknown as string;
      nextCards[carouselEditorIndex] = safe;
      const nextJson = JSON.stringify(nextCards);
      
      // СРАЗУ сохраняем в сообщение
      const nextContent = replaceTagContent(messageContent, 'SUGGEST_CAROUSEL', nextJson);
      onUpdateMessageContent(messageId, nextContent);
    } finally {
      setCarouselEditorSaving(false);
      setCarouselEditorOpen(false);
    }
  };

  const handleDownloadCarouselCard = async (idx: number) => {
    if (isDownloadingCarousel) return;
    const json = carouselDraftJson ?? carouselOriginalJson;
    if (!json) return;
    const cards = parseCarouselCards(json);
    const html = cards?.[idx];
    if (!html) return;

    setIsDownloadingCarousel(true);
    try {
      const el = document.getElementById(`${carouselExportId}-${idx}`);
      if (!(el instanceof HTMLElement)) return;
      // Карусели обычно 1080x1350
      const dataUrl = await toPng(el, { width: 1080, height: 1350 });
      const link = document.createElement('a');
      link.download = `carousel-card-${idx + 1}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error('Failed to download carousel card', e);
    } finally {
      setIsDownloadingCarousel(false);
    }
  };

  const handleCarouselCommitToMessage = () => {
    if (!onUpdateMessageContent) return;
    if (!messageId) return;
    const nextJson = carouselDraftJson ?? carouselOriginalJson;
    if (!nextJson) return;
    const nextContent = replaceTagContent(messageContent, 'SUGGEST_CAROUSEL', nextJson);
    onUpdateMessageContent(messageId, nextContent);
    setCarouselDirty(false);
  };

  const handleCarouselResetDraft = () => {
    setCarouselDraftJson(carouselOriginalJson);
    setCarouselDirty(false);
  };

  const sanitizedCoverCardHtml = React.useMemo(() => {
    if (!coverCardHtml) return null;
    const config: import('dompurify').Config = {
      ALLOWED_TAGS: [
        'div', 'span', 'p', 'header', 'footer', 'main', 'section', 'article',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'strong', 'em', 'b', 'i', 'u', 's', 'small', 'mark',
        'sup', 'sub',
        'ul', 'ol', 'li',
        'code', 'pre', 'blockquote',
        'br', 'hr',
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
        'img',
        // SVG элементы
        'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse',
        'g', 'defs', 'use', 'text', 'tspan', 'textPath', 'clipPath', 'mask',
        'linearGradient', 'radialGradient', 'stop', 'pattern', 'image',
      ],
      ALLOWED_ATTR: [
        'style', 'src', 'alt',
        // SVG атрибуты
        'viewBox', 'xmlns', 'xmlns:xlink', 'width', 'height', 'x', 'y',
        'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
        'd', 'cx', 'cy', 'r', 'rx', 'ry', 'points', 'x1', 'y1', 'x2', 'y2',
        'transform', 'opacity', 'fill-opacity', 'stroke-opacity',
        'clip-path', 'mask', 'id', 'class',
      ],
      FORBID_TAGS: ['script', 'style', 'link', 'canvas', 'video', 'audio', 'source', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'option', 'button', 'meta', 'base'],
      FORBID_ATTR: ['on*', 'href', 'srcset'],
      RETURN_TRUSTED_TYPE: false,
    };
    return DOMPurify.sanitize(coverCardHtml, config) as unknown as string;
  }, [coverCardHtml]);

  return (
    <div className="mt-3 max-w-full overflow-hidden min-w-0">
      {/* Очищенный текст сообщения */}
      {cleanContent && (
        <div className="mb-3 max-w-full overflow-hidden min-w-0">
          <span className="whitespace-pre-wrap">{cleanContent}</span>
        </div>
      )}

      {/* Предложения изменений */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4 max-w-full overflow-hidden min-w-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Предложения
            </span>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
          >
            {showPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            <span>{showPreview ? 'Скрыть' : 'Показать'}</span>
          </button>
        </div>

        {/* Список предложений */}
        <div className="space-y-3 max-w-full overflow-hidden min-w-0">
          {Object.entries(suggestions).map(([field, value]) => {
            const fieldKey = field as keyof PostSuggestion;
            const hasAppliedChanges = appliedChanges?.[field];
            const originalValue = originalValues && fieldKey in originalValues ? originalValues[fieldKey as keyof PostContext] : undefined;
            const isCarousel = field === 'carouselHtmlJson';
            const effectiveCarouselJson = isCarousel ? (carouselDraftJson ?? String(value)) : null;
            const isCoverCard = field === 'coverCardHtml';
            const effectiveCoverHtml = isCoverCard ? String(value) : null;
            
            return (
              <div key={field} className="space-y-2 max-w-full overflow-hidden min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleFieldToggle(field)}
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        selectedFields[field]
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {selectedFields[field] && <Check className="w-3 h-3" />}
                    </button>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {getFieldLabel(field)}
                    </span>
                    {hasAppliedChanges && (
                      <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                        Применено
                      </span>
                    )}
                  </div>
                  
                  {/* Кнопка отмены изменений */}
                  {hasAppliedChanges && onRevert && (
                    <button
                      onClick={() => handleRevert(fieldKey)}
                      className="flex items-center space-x-1 text-xs text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200 transition-colors"
                      title="Отменить изменения"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>Отменить</span>
                    </button>
                  )}
                </div>

                {showPreview && (
                  <div className="ml-0 md:ml-6 space-y-2 max-w-full overflow-hidden min-w-0">
                    {/* Предлагаемое значение */}
                    <div className="w-full">
                      <div className={`rounded ${
                        field === 'carouselHtmlJson' || field === 'coverCardHtml'
                          ? 'bg-transparent' 
                          : 'text-xs md:text-sm text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 p-2 border-l-2 border-blue-500'
                      }`}>
                        {field === 'carouselHtmlJson'
                          ? (
                            <div className="space-y-2">
                              <CarouselCardsPreview
                                carouselHtmlJson={effectiveCarouselJson || String(value)}
                                onEditCard={onUpdateMessageContent ? handleCarouselEditCard : undefined}
                                onDownloadCard={handleDownloadCarouselCard}
                                index={carouselPreviewIndex}
                                onIndexChange={setCarouselPreviewIndex}
                              />

                              {/* Скрытый слой для экспорта карточек */}
                              <div className="fixed left-[-10000px] top-0 opacity-0 pointer-events-none" aria-hidden="true">
                                {parseCarouselCards(effectiveCarouselJson || String(value))?.map((html, idx) => (
                                  <div
                                    key={idx}
                                    id={`${carouselExportId}-${idx}`}
                                    style={{ width: 1080, height: 1350 }}
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, domPurifyConfig) as unknown as string }}
                                  />
                                ))}
                              </div>
                            </div>
                          )
                          : field === 'coverCardHtml'
                            ? (
                              <div className="space-y-2">
                                <button
                                  type="button"
                                  onClick={() => setCoverLightboxOpen(true)}
                                  className="w-full max-w-[240px] aspect-[9/16] overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-left hover:ring-2 hover:ring-blue-500 transition-all active:scale-95 shadow-sm"
                                  aria-label="Просмотреть обложку"
                                >
                                  <div
                                    style={{ width: 1080, height: 1920, transform: 'scale(0.2222)', transformOrigin: 'top left', visibility: fontsReady ? 'visible' : 'hidden' }}
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(effectiveCoverHtml || String(value), {
                                        ALLOWED_TAGS: [
                                          'div', 'span', 'p', 'header', 'footer', 'main', 'section', 'article',
                                          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                                          'strong', 'em', 'b', 'i', 'u', 's', 'small', 'mark',
                                          'sup', 'sub',
                                          'ul', 'ol', 'li',
                                          'code', 'pre', 'blockquote',
                                          'br', 'hr',
                                          'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
                                          'img',
                                          // SVG элементы
                                          'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse',
                                          'g', 'defs', 'use', 'text', 'tspan', 'textPath', 'clipPath', 'mask',
                                          'linearGradient', 'radialGradient', 'stop', 'pattern', 'image',
                                        ],
                                        ALLOWED_ATTR: [
                                          'style', 'src', 'alt',
                                          // SVG атрибуты
                                          'viewBox', 'xmlns', 'xmlns:xlink', 'width', 'height', 'x', 'y',
                                          'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
                                          'd', 'cx', 'cy', 'r', 'rx', 'ry', 'points', 'x1', 'y1', 'x2', 'y2',
                                          'transform', 'opacity', 'fill-opacity', 'stroke-opacity',
                                          'clip-path', 'mask', 'id', 'class',
                                        ],
                                        FORBID_TAGS: ['script', 'style', 'link', 'canvas', 'video', 'audio', 'source', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'option', 'button', 'meta', 'base'],
                                        FORBID_ATTR: ['on*', 'href', 'srcset'],
                                        RETURN_TRUSTED_TYPE: false,
                                      }) as unknown as string
                                    }}
                                  />
                                </button>

                                {/* Скрытый слой для экспорта PNG */}
                                <div className="fixed left-[-10000px] top-0 opacity-0 pointer-events-none" aria-hidden="true">
                                  <div
                                    id={coverExportRootId}
                                    style={{ width: 1080, height: 1920 }}
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(effectiveCoverHtml || String(value), {
                                        ALLOWED_TAGS: [
                                          'div', 'span', 'p', 'header', 'footer', 'main', 'section', 'article',
                                          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                                          'strong', 'em', 'b', 'i', 'u', 's', 'small', 'mark',
                                          'sup', 'sub',
                                          'ul', 'ol', 'li',
                                          'code', 'pre', 'blockquote',
                                          'br', 'hr',
                                          'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
                                          'img',
                                          // SVG элементы
                                          'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse',
                                          'g', 'defs', 'use', 'text', 'tspan', 'textPath', 'clipPath', 'mask',
                                          'linearGradient', 'radialGradient', 'stop', 'pattern', 'image',
                                        ],
                                        ALLOWED_ATTR: [
                                          'style', 'src', 'alt',
                                          // SVG атрибуты
                                          'viewBox', 'xmlns', 'xmlns:xlink', 'width', 'height', 'x', 'y',
                                          'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
                                          'd', 'cx', 'cy', 'r', 'rx', 'ry', 'points', 'x1', 'y1', 'x2', 'y2',
                                          'transform', 'opacity', 'fill-opacity', 'stroke-opacity',
                                          'clip-path', 'mask', 'id', 'class',
                                        ],
                                        FORBID_TAGS: ['script', 'style', 'link', 'canvas', 'video', 'audio', 'source', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'option', 'button', 'meta', 'base'],
                                        FORBID_ATTR: ['on*', 'href', 'srcset'],
                                        RETURN_TRUSTED_TYPE: false,
                                      }) as unknown as string
                                    }}
                                  />
                                </div>
                              </div>
                            )
                            : <div className="whitespace-pre-wrap">{value}</div>}
                      </div>
                    </div>
                    
                    {/* Исходное значение (если есть изменения) */}
                    {hasAppliedChanges && originalValue && (
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Исходное значение:</div>
                        <div className={`rounded ${
                          field === 'carouselHtmlJson' 
                            ? 'bg-transparent' 
                            : 'text-xs md:text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 p-2 border-l-2 border-gray-400 whitespace-pre-wrap'
                        }`}>
                          {field === 'carouselHtmlJson' && typeof originalValue === 'string'
                            ? <CarouselCardsPreview carouselHtmlJson={originalValue} />
                            : typeof originalValue === 'string' 
                              ? originalValue 
                              : JSON.stringify(originalValue, null, 2)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Кнопки действий */}
        <div className="flex items-center justify-end space-x-2 mt-4 pt-3 border-t border-blue-200 dark:border-blue-700">
          <button
            onClick={handleApply}
            disabled={disabled || !hasSelectedFields}
            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs rounded-md transition-colors flex items-center space-x-1"
          >
            <Check className="w-3 h-3" />
            <span>Применить</span>
          </button>
        </div>
      </div>

      {/* Просмотр обложки на весь экран (Lightbox) */}
      {coverLightboxOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[15000]">
          <div
            className="absolute inset-0 bg-black/85"
            onClick={() => setCoverLightboxOpen(false)}
            aria-label="Закрыть просмотр"
          />

          <div className="absolute inset-0 flex flex-col">
            <div className="relative flex-1 overflow-hidden">
              <div 
                className="absolute inset-0 flex items-center justify-center p-3 touch-none overflow-hidden"
                style={{ overscrollBehavior: 'none' }}
              >
                <div className="w-full flex justify-center py-12">
                  {(() => {
                    const fitScale = Math.min(
                      (window.innerWidth - 24) / 1080,
                      (window.innerHeight - 240) / 1920
                    );
                    const fitW = Math.max(1, Math.floor(1080 * fitScale));
                    const fitH = Math.max(1, Math.floor(1920 * fitScale));
                    return (
                              <div className="w-fit">
                                <div
                                  className="relative bg-black"
                                  style={{ width: fitW, height: fitH }}
                                >
                                  <div
                                    className="absolute left-0 top-0"
                                    style={{
                                      width: 1080,
                                      height: 1920,
                                      transform: `scale(${fitScale})`,
                                      transformOrigin: 'top left',
                                      visibility: fontsReady ? 'visible' : 'hidden',
                                      fontSize: '16px',
                                      lineHeight: 'normal',
                                      color: '#000000',
                                      textAlign: 'left',
                                    }}
                                    dangerouslySetInnerHTML={{ __html: sanitizedCoverCardHtml || '' }}
                                  />
                                </div>
                                {!fontsReady && (
                                  <div className="mt-2 text-xs text-white/60 text-center">
                                    Загружаем шрифты…
                                  </div>
                                )}
                              </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Кнопки внизу */}
            <div className="px-4 py-4 pb-8 flex items-center justify-center gap-3">
              {onUpdateMessageContent && (
                <button
                  type="button"
                  onClick={() => {
                    setCoverLightboxOpen(false);
                    handleCoverEdit();
                  }}
                  className="flex-1 max-w-[160px] px-4 py-2.5 text-sm font-medium rounded-xl text-white border border-white/25 bg-white/10 hover:bg-white/15 backdrop-blur transition-colors"
                  aria-label="Редактировать обложку"
                >
                  Редактировать
                </button>
              )}
              <button
                type="button"
                onClick={handleDownloadCover}
                className="flex-1 max-w-[160px] px-4 py-2.5 text-sm font-medium rounded-xl text-white border border-white/25 bg-white/10 hover:bg-white/15 backdrop-blur transition-colors"
                aria-label="Скачать обложку"
              >
                {isDownloadingCover ? 'Скачиваем…' : 'Скачать'}
              </button>
              <button
                type="button"
                onClick={() => setCoverLightboxOpen(false)}
                className="flex-1 max-w-[160px] px-4 py-2.5 text-sm font-medium rounded-xl text-white border border-white/25 bg-white/10 hover:bg-white/15 backdrop-blur transition-colors"
                aria-label="Закрыть"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Простой редактор HTML карточки карусели (для сообщений чата) */}
      <RichModalEditor
        open={carouselEditorOpen}
        html={carouselEditorHtml}
        onClose={() => setCarouselEditorOpen(false)}
        onSave={handleCarouselSaveCard}
        isSaving={carouselEditorSaving}
      />

      {/* Простой редактор HTML обложки (для сообщений чата) */}
      <RichModalEditor
        open={coverEditorOpen}
        html={coverEditorHtml}
        onClose={() => setCoverEditorOpen(false)}
        onSave={handleCoverSave}
        isCover={true}
      />
    </div>
  );
}; 